/*
 * Purpose: Mission results summary bar — pinned to bottom of map well after runs
 * Last modified: 2026-07-10 | Agent
 * Completeness: 100
 */
import { AlertCircle, CheckCircle, ChevronUp, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ExecutionContext } from '@/services/executor';
import type { Workflow } from '@/stores/workflowStore';
import { getNodeLabel } from '@/lib/workflowUtils';
import { summarizeExecutionResult } from '@/lib/executionResultSummary';
import { cn } from '@/lib/utils';

interface MissionMapResultsBarProps {
  results: ExecutionContext[];
  isExecuting: boolean;
  workflow: Workflow | null;
  onExpandDetails?: () => void;
}

function formatOutputPreview(
  output: unknown,
  node?: import('@/stores/workflowStore').Node
): string | null {
  const { headline, detail } = summarizeExecutionResult(output, node);
  const combined = detail ? `${headline} — ${detail}` : headline;
  return combined.length > 140 ? `${combined.slice(0, 137)}…` : combined;
}

function buildResultsHeadline(
  results: ExecutionContext[],
  workflow: Workflow | null,
  isExecuting: boolean
): { status: 'running' | 'success' | 'partial' | 'idle'; headline: string; detail: string | null } {
  if (isExecuting) {
    return {
      status: 'running',
      headline: 'Running Mission flow…',
      detail: workflow ? `${workflow.name} is executing on the map.` : null,
    };
  }

  if (results.length === 0) {
    return { status: 'idle', headline: 'No run yet', detail: 'Start the flow to see results here.' };
  }

  const failed = results.filter((r) => r.errors.length > 0).length;
  const passed = results.length - failed;
  const last = results[results.length - 1];
  const lastNode = workflow?.nodes.find((n) => n.id === last.nodeId);
  const lastLabel = lastNode ? getNodeLabel(lastNode) : 'Last block';

  let headline: string;
  if (failed === 0) {
    headline = `${passed} block${passed === 1 ? '' : 's'} ran · all passed`;
  } else if (passed === 0) {
    headline = `${failed} block${failed === 1 ? '' : 's'} failed`;
  } else {
    headline = `${passed} passed · ${failed} failed`;
  }

  const preview = formatOutputPreview(last.output, lastNode);
  const detail =
    last.errors.length > 0
      ? `${lastLabel}: ${last.errors[0]}`
      : preview
        ? `${lastLabel}: ${preview}`
        : `${lastLabel} completed`;

  return {
    status: failed === 0 ? 'success' : 'partial',
    headline,
    detail,
  };
}

export function MissionMapResultsBar({
  results,
  isExecuting,
  workflow,
  onExpandDetails,
}: MissionMapResultsBarProps) {
  const { status, headline, detail } = buildResultsHeadline(results, workflow, isExecuting);
  const showBar = isExecuting || results.length > 0;

  if (!showBar) return null;

  return (
    <div
      className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex justify-center p-2 sm:p-3"
      role="region"
      aria-label="Mission results summary"
      aria-live="polite"
    >
      <div
        className={cn(
          'pointer-events-auto flex w-full max-w-2xl items-center gap-2.5 rounded-xl border px-3 py-2 shadow-lg backdrop-blur-sm sm:gap-3 sm:px-4 sm:py-2.5',
          status === 'partial'
            ? 'border-destructive/40 bg-destructive/5'
            : 'border-border/70 bg-card/95'
        )}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted/60">
          {status === 'running' ? (
            <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden />
          ) : status === 'partial' ? (
            <AlertCircle className="h-4 w-4 text-destructive" aria-hidden />
          ) : (
            <CheckCircle className="h-4 w-4 text-ab-green-sea dark:text-ab-green-glow" aria-hidden />
          )}
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Mission results
          </p>
          <p className="truncate text-sm font-medium text-foreground">{headline}</p>
          {detail && (
            <p className="truncate text-xs text-muted-foreground" title={detail}>
              {detail}
            </p>
          )}
        </div>

        {onExpandDetails && results.length > 0 && !isExecuting && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="h-8 shrink-0 gap-1 text-xs"
            onClick={onExpandDetails}
          >
            <ChevronUp className="h-3.5 w-3.5" />
            Details
          </Button>
        )}
      </div>
    </div>
  );
}

export default MissionMapResultsBar;
